declare global {
  interface Window {
    ENV?: { [key: string]: string };
    getEnvValue: (name: string, throwIfMissing?: boolean) => string;
  }
}

let envOverrides: Record<string, string> = {};
export function setEnvOverrides(overrides: Record<string, string>) {
  envOverrides = overrides;
}

// Set on window so this is accessible from playwright
window.getEnvValue = function (name: string, throwIfMissing = true) {
  if (throwIfMissing && !window.ENV) {
    throw new Error("window.ENV not present. Is the server running?");
  }

  const env = {
    ...(window.ENV || {}),
    ...envOverrides,
  };

  if (throwIfMissing && !Object.prototype.hasOwnProperty.call(env, name)) {
    throw new Error(`No "${name}" environment variable`);
  }

  return env[name];
};

/**
 * Get an environment variable. If it doesn't exist, halt with an error.
 *
 * Use this in cases where the app should stop execution and crash if the variable doesn't exist.
 */
export function envWithThrowIfMissing(name: string): string {
  return window.getEnvValue(name);
}

/**
 * Get an environment variable. If it doesn't exist, return a default value or undefined.
 *
 * Use this in cases where the app should continue if the variable doesn't exist, such as for
 * feature flags.
 */
export function envWithDefault(name: string): string | undefined;
export function envWithDefault(name: string, defaultValue: string): string;
export function envWithDefault(name: string, defaultValue?: string) {
  return window.getEnvValue(name, false) ?? defaultValue;
}

export function featureEnabled(name: string): boolean {
  return envWithDefault(name, "false") === "true";
}

export function logDev(message: string, ...optionalParams: unknown[]) {
  if (alongEnv() === "development") {
    console.log(message, ...optionalParams);
  }
}

const environments = [
  "development",
  "test",
  "staging",
  "review",
  "production",
] as const;
type AlongEnv = (typeof environments)[number];

function isAlongEnv(val?: string): val is AlongEnv {
  if (!val || typeof val !== "string") {
    return false;
  }

  return environments.includes(val as AlongEnv);
}

export function alongEnv() {
  // If server isn't available to set the env, we look for it in the environment. Sadly
  // we need an extra environment variable because storybook ignores vars that aren't prefixed
  // with STORYBOOK when building.
  const connectionsEnv =
    envWithDefault("CONNECTIONS_ENV") ||
    process.env.CONNECTIONS_ENV ||
    process.env.STORYBOOK_ENV;
  if (!connectionsEnv) {
    throw new Error(`CONNECTIONS_ENV is not set`);
  }

  if (!isAlongEnv(connectionsEnv)) {
    throw new Error(`unexpected connection env: ${connectionsEnv}`);
  }
  return connectionsEnv;
}
